import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Eye, Play, Heart, MessageCircle, Repeat2, TrendingUp, BarChart3,
  Youtube, Instagram, Facebook, Linkedin, AtSign, Flame, Calendar, Layers,
  Search, ExternalLink,
} from 'lucide-react';
import { GlassCard } from '../_shared/GlassCard';
import { Footer } from '../_shared/Footer';
import { ALL_COMMENTS } from './commentsJune';

/* ----------------------------------------------------------------------------
   DATA — public counts via Apify, window 28 Mar 2026 → 28 Jun 2026 (~3 months).
   IG @thesachindahiya · YouTube @scalingedtechs · FB /dahiyaconsulting ·
   LinkedIn company 99402027 · X @scalingedtechs.
   These are PUBLIC view/play counts — NOT private reach/saves/shares/impressions.
   Raw: clients-current/sachin-dahiya-scaleed/research/*-2026-06-28.json
---------------------------------------------------------------------------- */

const TOTAL_VIEWS = 73579;     // YT 39,491 + FB 26,743 + IG 7,225 + X ~120
const TOTAL_POSTS = 278;       // IG 71 + YT 74 + FB 68 + LI 60 + X 5
const TOTAL_ENGAGEMENT = 761;  // likes + comments + reactions + reposts (public)

const MONTHS = [
  { month: 'Mar', label: "Mar '26", ig: 341, yt: 5028, fb: 272, partial: true },
  { month: 'Apr', label: "Apr '26", ig: 2264, yt: 14178, fb: 14225 },
  { month: 'May', label: "May '26", ig: 3108, yt: 14646, fb: 9696 },
  { month: 'Jun', label: "Jun '26", ig: 1512, yt: 5639, fb: 2550, partial: true },
];

const COMMENT_KEYWORDS = [
  { kw: 'AI', count: 15, color: 'from-purple-500 to-fuchsia-400' },
  { kw: 'campus', count: 4, color: 'from-blue-500 to-indigo-400' },
  { kw: 'CaseStudy', count: 3, color: 'from-emerald-500 to-teal-400' },
  { kw: 'Scale', count: 3, color: 'from-amber-500 to-orange-400' },
  { kw: 'Claude', count: 3, color: 'from-rose-500 to-pink-400' },
  { kw: 'League', count: 3, color: 'from-cyan-500 to-blue-400' },
  { kw: 'CAC', count: 2, color: 'from-slate-500 to-slate-400' },
];

const NOTABLE_COMMENTS = [
  { user: '@chandparakram', text: "Hello Sachin, offer looks solid. I'm a high-ticket closer - work with one operator at a time...", tag: 'Inbound partner' },
  { user: '@jitendrarathore.1', text: "Sir, aapko DM kar rakha hai regarding something. Do check.", tag: 'Lead → DM' },
  { user: '@catsofthehub', text: "You should do a night school with @thehubbengaluru", tag: 'Collab ask' },
];

const igReels = [
  { title: "Comment 'Scale' to join this Global EdTech Community", metric: '585', gradient: 'from-purple-600 via-violet-500 to-fuchsia-400', url: 'https://www.instagram.com/p/DYwaoIIzv-F/', emoji: '🌐' },
  { title: 'Indian EdTech has two leagues - unicorns vs profit', metric: '298', gradient: 'from-emerald-500 via-teal-500 to-cyan-400', url: 'https://www.instagram.com/p/DYXKhv3kjLF/', emoji: '🏆' },
  { title: 'upGrad acquired Unacademy at ~90% below valuation', metric: '242', gradient: 'from-amber-500 via-orange-500 to-red-500', url: 'https://www.instagram.com/p/DYMym0dj8uO/', emoji: '📉' },
  { title: 'PhysicsWallah went public at 30,000+ crore', metric: '186', gradient: 'from-blue-600 via-indigo-500 to-violet-500', url: 'https://www.instagram.com/p/DZfeKIgNhbX/', emoji: '🚀' },
  { title: 'Everyone says Indian EdTech is dead. The math says no', metric: '158', gradient: 'from-rose-600 via-red-500 to-orange-400', url: 'https://www.instagram.com/p/DZce384CYbe/', emoji: '🧮' },
];

const ytReels = [
  { title: 'One of the most underrated ways to get clients', metric: '1,669', gradient: 'from-red-600 via-rose-500 to-orange-400', url: 'https://www.youtube.com/shorts/y4RKJYLFMUI', emoji: '🎯' },
  { title: 'The math behind the $100 Million Dollar launch', metric: '1,612', gradient: 'from-amber-500 via-orange-500 to-red-500', url: 'https://www.youtube.com/shorts/lhEeB22nqv8', emoji: '💯' },
  { title: 'Meta AI vs Manual Targeting', metric: '1,522', gradient: 'from-blue-600 via-indigo-500 to-violet-500', url: 'https://www.youtube.com/shorts/jTf53IMzvuQ', emoji: '🤖' },
  { title: 'This Boring EdTech model making 20 lakhs a month', metric: '1,439', gradient: 'from-emerald-500 via-teal-500 to-cyan-400', url: 'https://www.youtube.com/shorts/si3JHfWONDQ', emoji: '💰' },
  { title: 'Stop running a Lala EdTech', metric: '1,370', gradient: 'from-red-700 via-red-500 to-pink-400', url: 'https://www.youtube.com/shorts/IZWwlOU_AgE', emoji: '🛑' },
];

const fbReels = [
  { title: "People don't care about budget when handed free cash flow", metric: '10,000', gradient: 'from-blue-600 via-indigo-500 to-violet-500', url: 'https://facebook.com/reel/1079430148090338/', emoji: '💵' },
  { title: 'The biggest pricing reset in performance marketing', metric: '6,100', gradient: 'from-amber-500 via-orange-500 to-red-500', url: 'https://facebook.com/reel/1220820006795950/', emoji: '📉' },
  { title: '60 seconds to build trust, not remorse', metric: '366', gradient: 'from-emerald-500 via-teal-500 to-cyan-400', url: 'https://facebook.com/reel/2008028740067012/', emoji: '⏱️' },
  { title: '50,000 followers earning less than someone with 2,000', metric: '338', gradient: 'from-rose-600 via-red-500 to-orange-400', url: 'https://facebook.com/reel/1447867966457647/', emoji: '👥' },
  { title: 'EdTech scaling reel · May 30', metric: '284', gradient: 'from-purple-600 via-violet-500 to-fuchsia-400', url: 'https://facebook.com/reel/4163754003889854/', emoji: '📈' },
];

const liPosts = [
  { title: 'This is not a prediction. AI is already replacing the roles.', reactions: 5, comments: 1, reposts: 1, gradient: 'from-cyan-700 via-blue-700 to-indigo-700', url: 'https://www.linkedin.com/posts/scale-ed_ai-automation-business-ugcPost-7455265955493883904-w9Gy' },
  { title: 'Two people saw the exact same price. One bought, one left.', reactions: 5, comments: 0, reposts: 0, gradient: 'from-blue-700 via-blue-600 to-indigo-600', url: 'https://www.linkedin.com/posts/scale-ed_sales-trust-business-activity-7454181486305595392-jOXE' },
  { title: 'Founders blame Google Ads for a revenue plateau. The real fix...', reactions: 4, comments: 0, reposts: 1, gradient: 'from-indigo-700 via-blue-600 to-cyan-500', url: 'https://www.linkedin.com/posts/scale-ed_edtech-ads-roas-activity-7462168389587718145-kQs3' },
  { title: 'Most founders think revenue is stuck because of their offer.', reactions: 4, comments: 0, reposts: 1, gradient: 'from-blue-800 via-indigo-700 to-purple-700', url: 'https://www.linkedin.com/posts/scale-ed_founder-business-growth-activity-7452680875693236224-c50W' },
  { title: 'People buy from honest people. Here is why.', reactions: 4, comments: 0, reposts: 1, gradient: 'from-slate-700 via-blue-700 to-indigo-600', url: 'https://www.linkedin.com/posts/scale-ed_business-scaling-edtech-activity-7453429407840403457-yCwt' },
  { title: 'Your reporting is killing your growth.', reactions: 3, comments: 0, reposts: 1, gradient: 'from-blue-600 via-indigo-600 to-violet-600', url: 'https://www.linkedin.com/posts/scale-ed_ai-scaling-business-activity-7453100955291516928-fhdv' },
];

/* ---------------------------------------------------------------- count-up hook */
const useCounter = (end, duration = 1400) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        const start = performance.now();
        const animate = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(end * eased));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return [count, ref];
};

const StatCard = ({ icon: Icon, iconColor, label, value, suffix = '', subtext, displayValue }) => {
  const [animatedValue, ref] = useCounter(value);
  return (
    <GlassCard className="flex flex-col justify-between min-h-[180px]">
      <div ref={ref}>
        <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
          <Icon size={22} />
        </div>
        <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
        <p className="text-4xl font-black text-white tracking-tight">
          {displayValue || <>{animatedValue.toLocaleString()}{suffix}</>}
        </p>
        {subtext && <p className="text-white/50 text-sm mt-3">{subtext}</p>}
      </div>
    </GlassCard>
  );
};

const ReelGrid = ({ reels, metricLabel = 'Views' }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
    {reels.map((reel, i) => (
      <a
        key={i}
        href={reel.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-[2rem] overflow-hidden transition-transform duration-300 hover:scale-[1.03]"
        style={{ aspectRatio: '9/16' }}
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${reel.gradient}`} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-5xl mb-3 opacity-30 group-hover:opacity-0 transition-opacity duration-300">{reel.emoji}</span>
        </div>
        <div className="absolute top-1/4 left-3 right-3">
          <div className="bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
            <p className="text-white text-xs sm:text-sm font-bold leading-tight text-center">{reel.title}</p>
          </div>
        </div>
        <div className="absolute top-3 left-3">
          <span className="bg-black/40 backdrop-blur-md text-white text-xs font-bold px-2.5 py-1 rounded-full border border-white/10">#{i + 1}</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white text-sm font-bold">{reel.metric}</p>
          <p className="text-white/50 text-[11px] font-mono uppercase tracking-wider">{metricLabel}</p>
        </div>
      </a>
    ))}
  </div>
);

const LinkedInGrid = ({ posts }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {posts.map((p, i) => (
      <a
        key={i}
        href={p.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-3xl overflow-hidden transition-transform duration-300 hover:scale-[1.02] min-h-[200px]"
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${p.gradient}`} />
        <div className="relative p-6 h-full flex flex-col justify-between min-h-[200px]">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Linkedin size={16} className="text-white/80" />
              <span className="text-white/60 text-xs font-mono">#{i + 1}</span>
            </div>
            <p className="text-white text-sm font-semibold leading-snug line-clamp-4">{p.title}</p>
          </div>
          <div className="flex items-center gap-4 mt-4 text-white/80 text-xs">
            <span className="flex items-center gap-1"><Heart size={14} />{p.reactions}</span>
            <span className="flex items-center gap-1"><MessageCircle size={14} />{p.comments}</span>
            <span className="flex items-center gap-1"><Repeat2 size={14} />{p.reposts}</span>
          </div>
        </div>
      </a>
    ))}
  </div>
);

/* ------------------------------------------------------ Month-by-month chart */
const fmtK = (n) => (n >= 1000 ? `${Math.round(n / 1000)}K` : `${n}`);

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-black/80 backdrop-blur-md border border-white/15 rounded-xl px-4 py-3 text-left">
      <p className="text-white font-bold text-sm mb-2">{d.label}{d.partial ? ' (partial)' : ''}</p>
      <p className="text-red-300 text-sm">YouTube · {d.yt.toLocaleString()} views</p>
      <p className="text-blue-300 text-sm mt-1">Facebook · {d.fb.toLocaleString()} views</p>
      <p className="text-purple-300 text-sm mt-1">Instagram · {d.ig.toLocaleString()} views</p>
    </div>
  );
};

const MonthlyProgression = () => (
  <section className="py-16 px-4">
    <div className="container mx-auto max-w-6xl">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4">
          <BarChart3 size={14} className="text-purple-300" />
          <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">Progression · Views by platform</span>
        </div>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">Month by Month</h2>
        <p className="text-white/50 max-w-2xl mx-auto">
          YouTube holds the steadiest baseline, Facebook carries the volume, and Instagram is the runway still building. June is a partial month (cut on the 28th).
        </p>
      </div>

      <GlassCard className="mb-10">
        <div className="h-[340px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={MONTHS} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="ytFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f87171" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#f87171" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="fbFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="igFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#c084fc" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#c084fc" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={fmtK} tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 12 }} axisLine={false} tickLine={false} width={48} />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.15)' }} />
              <Legend wrapperStyle={{ paddingTop: 8 }} iconType="circle" formatter={(v) => <span className="text-white/60 text-xs">{v === 'ig' ? 'Instagram' : v === 'yt' ? 'YouTube' : 'Facebook'}</span>} />
              <Area type="monotone" dataKey="yt" stroke="#f87171" strokeWidth={3} fill="url(#ytFill)" dot={{ r: 4, fill: '#f87171', strokeWidth: 0 }} activeDot={{ r: 6 }} />
              <Area type="monotone" dataKey="fb" stroke="#60a5fa" strokeWidth={3} fill="url(#fbFill)" dot={{ r: 4, fill: '#60a5fa', strokeWidth: 0 }} activeDot={{ r: 6 }} />
              <Area type="monotone" dataKey="ig" stroke="#c084fc" strokeWidth={3} fill="url(#igFill)" dot={{ r: 4, fill: '#c084fc', strokeWidth: 0 }} activeDot={{ r: 6 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="text-white/30 text-xs text-center mt-4">Total monthly public views per platform. LinkedIn & X excluded (no public view metric / negligible).</p>
      </GlassCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {MONTHS.map((m) => {
          const total = m.ig + m.yt + m.fb;
          return (
            <GlassCard key={m.month} className={m.partial ? 'opacity-90' : ''}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Calendar size={15} className="text-white/40" />
                  <h3 className="text-base font-bold text-white">{m.label}</h3>
                </div>
                {m.partial && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 bg-white/5 px-2 py-0.5 rounded-full border border-white/10">Partial</span>
                )}
              </div>
              <div className="mb-4">
                <p className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-1">Combined Views</p>
                <p className="text-3xl font-black text-white">{total.toLocaleString()}</p>
              </div>
              <div className="space-y-2.5 pt-3 border-t border-white/5">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs text-red-300"><Youtube size={12} /> YouTube</span>
                  <span className="text-white/80 text-sm font-semibold">{m.yt.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs text-blue-300"><Facebook size={12} /> Facebook</span>
                  <span className="text-white/80 text-sm font-semibold">{m.fb.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs text-purple-300"><Instagram size={12} /> Instagram</span>
                  <span className="text-white/80 text-sm font-semibold">{m.ig.toLocaleString()}</span>
                </div>
              </div>
            </GlassCard>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------ Comments / lead funnel */
const CommentsSection = () => {
  const max = Math.max(...COMMENT_KEYWORDS.map((k) => k.count));
  return (
    <section className="py-16 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-amber-500/20 text-amber-300 p-2.5 rounded-xl"><MessageCircle size={22} /></div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">Instagram Comments — the lead funnel</h2>
        </div>
        <p className="text-white/50 max-w-3xl mb-10">
          Comments aren't vanity here. Most are <span className="text-amber-300 font-semibold">CTA keyword-triggers</span> — viewers
          typing the keyword to pull a resource into their DMs. That means the comment → DM automation is firing, and real
          inbound is showing up in the thread.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* keyword bars */}
          <GlassCard>
            <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-6">Most-commented keywords (CTA triggers)</p>
            <div className="space-y-4">
              {COMMENT_KEYWORDS.map((k) => (
                <div key={k.kw}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-white font-bold text-sm">{k.kw}</span>
                    <span className="text-white/60 text-sm font-mono">{k.count}×</span>
                  </div>
                  <div className="h-3 w-full rounded-full bg-white/5 overflow-hidden">
                    <div className={`h-full rounded-full bg-gradient-to-r ${k.color}`} style={{ width: `${Math.max((k.count / max) * 100, 6)}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-white/30 text-xs mt-6">56 comments captured across 45 posts. Each keyword = a viewer pulling a lead magnet via comment-to-DM.</p>
          </GlassCard>

          {/* notable inbound */}
          <div className="space-y-4">
            {NOTABLE_COMMENTS.map((c, i) => (
              <GlassCard key={i}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-bold text-sm">{c.user}</span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-300 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-500/30">{c.tag}</span>
                </div>
                <p className="text-white/75 text-sm leading-relaxed italic">"{c.text}"</p>
              </GlassCard>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------- All comments — filter + sort */
const CATEGORY_META = {
  all:     { label: 'All',              chip: 'bg-white/10 text-white border-white/20' },
  cta:     { label: 'CTA triggers',     chip: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  inbound: { label: 'Inbound leads',    chip: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  praise:  { label: 'Praise',           chip: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
};

const TAG_STYLE = {
  cta: 'text-amber-300 bg-amber-500/15 border-amber-500/30',
  inbound: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30',
  praise: 'text-rose-300 bg-rose-500/15 border-rose-500/30',
};

const fmtDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

const CommentsExplorer = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const counts = useMemo(() => {
    const c = { all: ALL_COMMENTS.length, cta: 0, inbound: 0, praise: 0 };
    ALL_COMMENTS.forEach((x) => { c[x.category] += 1; });
    return c;
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = ALL_COMMENTS.filter((c) => {
      if (category !== 'all' && c.category !== category) return false;
      if (!q) return true;
      return (
        c.text.toLowerCase().includes(q) ||
        c.owner.toLowerCase().includes(q) ||
        (c.keyword || '').toLowerCase().includes(q) ||
        c.postCaption.toLowerCase().includes(q)
      );
    });
    list = [...list].sort((a, b) => {
      if (sortBy === 'newest') return b.date.localeCompare(a.date);
      if (sortBy === 'oldest') return a.date.localeCompare(b.date);
      return (b.likes - a.likes) || b.date.localeCompare(a.date);
    });
    return list;
  }, [query, category, sortBy]);

  return (
    <section className="py-16 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-amber-500/20 text-amber-300 p-2.5 rounded-xl"><MessageCircle size={22} /></div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">Every comment, itemized</h2>
          <span className="text-white/40 text-sm font-mono">{ALL_COMMENTS.length} captured</span>
        </div>
        <p className="text-white/50 max-w-3xl mb-8">
          All public comments scraped across the 45 Instagram posts in the window — searchable, filterable by type,
          sortable by date or likes. Each card links to the post it was left on.
        </p>

        {/* controls */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search comments, users, keywords…"
              className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-purple-400/50 focus:bg-white/10 transition-colors"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(CATEGORY_META).map(([key, meta]) => (
              <button
                key={key}
                onClick={() => setCategory(key)}
                className={`text-xs font-bold uppercase tracking-widest px-3.5 py-2 rounded-full border transition-all ${
                  category === key ? meta.chip : 'bg-transparent text-white/40 border-white/10 hover:text-white/70 hover:border-white/25'
                }`}
              >
                {meta.label} · {counts[key]}
              </button>
            ))}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white/80 focus:outline-none focus:border-purple-400/50 cursor-pointer [&>option]:bg-slate-900"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="likes">Most liked</option>
          </select>
        </div>

        <p className="text-white/30 text-xs mb-4 font-mono">Showing {visible.length} of {ALL_COMMENTS.length}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visible.map((c) => (
            <div key={c.id} className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:bg-white/[0.07] hover:border-white/20 transition-colors">
              <div className="flex items-start justify-between gap-3 mb-2.5">
                <span className="text-white font-bold text-sm break-all">@{c.owner}</span>
                <span className={`shrink-0 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${TAG_STYLE[c.category]}`}>
                  {c.category === 'cta' ? `CTA · ${c.keyword}` : CATEGORY_META[c.category].label}
                </span>
              </div>
              <p className="text-white/85 text-sm leading-relaxed mb-3">"{c.text}"</p>
              <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/5">
                <div className="flex items-center gap-4 text-white/40 text-xs">
                  <span className="flex items-center gap-1"><Calendar size={12} />{fmtDate(c.date)}</span>
                  <span className="flex items-center gap-1"><Heart size={12} />{c.likes}</span>
                  {c.replies > 0 && <span className="flex items-center gap-1"><MessageCircle size={12} />{c.replies}</span>}
                </div>
                <a
                  href={c.postUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-purple-300 hover:text-purple-200 text-xs font-semibold transition-colors shrink-0"
                  title={c.postCaption}
                >
                  View post <ExternalLink size={11} />
                </a>
              </div>
              {c.postCaption && (
                <p className="text-white/30 text-[11px] mt-2 truncate">on: {c.postCaption}</p>
              )}
            </div>
          ))}
        </div>

        {visible.length === 0 && (
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl text-center py-12">
            <p className="text-white/50 text-sm">No comments match — clear the search or switch filters.</p>
          </div>
        )}

        <p className="text-white/30 text-xs mt-6">
          Note: these are the comments the public scrape could capture (~15 per post cap). Posts with heavy comment
          activity — like the "AI builds full courses" reel with 85 reported comments — have more than shown here.
        </p>
      </div>
    </section>
  );
};

/* --------------------------------------------------------------- divider */
const Divider = () => (
  <div className="max-w-6xl mx-auto px-4"><div className="border-t border-white/10" /></div>
);

/* ----------------------------------------------------------------- Page */
export default function SachinReportJune() {
  useEffect(() => {
    const pageTitle = 'Sachin – 3-Month Performance Report | SocialSEO';
    document.title = pageTitle;
    const ensureMeta = (selector, attributes) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attributes).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute('content', pageTitle);
    };
    ensureMeta('meta[property="og:title"]', { property: 'og:title' });
    ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' });
  }, []);

  return (
    <div className="font-sans leading-relaxed text-white relative" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
      `}</style>

      <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-between items-center bg-transparent pointer-events-none">
        <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
          <span className="font-bold tracking-tight text-purple-300"><span className="text-white">Social</span>SEO</span>
        </div>
        <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
          <span className="text-sm font-medium text-white/80">Mar 28 → Jun 28, 2026</span>
        </div>
      </header>

      <div className="relative z-10">
        <main>
          {/* Hero */}
          <section className="min-h-[60vh] flex flex-col justify-center px-4 pt-24 pb-8">
            <div className="max-w-6xl mx-auto w-full space-y-4">
              <p className="text-purple-400 font-bold text-sm uppercase tracking-widest">Social SEO · 3-Month Report</p>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-100 to-white leading-tight">
                Sachin Dahiya<br />Performance Report
              </h1>
              <p className="text-lg text-white/60 max-w-2xl">
                Every reel, short, carousel and post across <span className="text-white font-semibold">Instagram, YouTube, Facebook, LinkedIn & X</span>, measured over the last 3 months.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-8">
                <StatCard icon={Eye} iconColor="bg-pink-500/20 text-pink-300" label="Total Public Views" value={TOTAL_VIEWS} subtext="YouTube + Facebook + Instagram + X" />
                <StatCard icon={Layers} iconColor="bg-purple-500/20 text-purple-300" label="Content Shipped" value={TOTAL_POSTS} subtext="Posts across 5 platforms · ~3/day" />
                <StatCard icon={Heart} iconColor="bg-amber-500/20 text-amber-300" label="Total Engagement" value={TOTAL_ENGAGEMENT} subtext="Likes + comments + reactions + reposts" />
              </div>
              <p className="text-white/30 text-xs max-w-2xl pt-4">
                Figures are public view/play counts, captured via automated scrape on 28 Jun 2026. Reach, saves, shares and impressions are private Insights (owner-only) and are not included.
              </p>
            </div>
          </section>

          <MonthlyProgression />
          <Divider />
          <CommentsSection />
          <CommentsExplorer />
          <Divider />

          {/* YOUTUBE */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-red-500/20 text-red-400 p-2.5 rounded-xl"><Youtube size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">YouTube Shorts Performance</h2>
                <span className="text-white/40 text-sm font-mono">@scalingedtechs</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
                <StatCard icon={Play} iconColor="bg-red-500/20 text-red-300" label="Videos Posted" value={74} subtext="69 Shorts + 5 long-form" />
                <StatCard icon={Eye} iconColor="bg-pink-500/20 text-pink-300" label="Total Views" value={39491} subtext="Public counter" />
                <StatCard icon={TrendingUp} iconColor="bg-emerald-500/20 text-emerald-300" label="Avg / Video" value={534} subtext="Steadiest of all platforms" />
                <StatCard icon={Flame} iconColor="bg-amber-500/20 text-amber-300" label="Best Short" value={1669} subtext="Underrated way to get clients" />
              </div>
              <div className="mb-6 flex items-center gap-3">
                <h3 className="text-xl font-bold text-white">Top 5 Shorts</h3>
                <span className="bg-red-500/20 text-red-300 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-red-500/30">YouTube</span>
              </div>
              <ReelGrid reels={ytReels} metricLabel="Views" />
            </div>
          </section>

          <Divider />

          {/* FACEBOOK */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-blue-500/20 text-blue-400 p-2.5 rounded-xl"><Facebook size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">Facebook Reels Performance</h2>
                <span className="text-white/40 text-sm font-mono">/dahiyaconsulting</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
                <StatCard icon={Play} iconColor="bg-blue-500/20 text-blue-300" label="Reels Posted" value={68} subtext="Last 3 months" />
                <StatCard icon={Eye} iconColor="bg-pink-500/20 text-pink-300" label="Total Views" value={26743} subtext="Reel-feed play counts" />
                <StatCard icon={TrendingUp} iconColor="bg-emerald-500/20 text-emerald-300" label="Avg / Reel" value={393} subtext="Across all reels" />
                <StatCard icon={Flame} iconColor="bg-amber-500/20 text-amber-300" label="Best Reel" value={10000} subtext="Free cash flow hook" />
              </div>
              <div className="mb-6 flex items-center gap-3">
                <h3 className="text-xl font-bold text-white">Top 5 Reels</h3>
                <span className="bg-blue-500/20 text-blue-300 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-blue-500/30">Facebook</span>
              </div>
              <ReelGrid reels={fbReels} metricLabel="Views" />
              <p className="text-white/40 text-xs mt-4 italic">View counts pulled from the Facebook Reels feed. Like/comment counts are not exposed by the public reels feed.</p>
            </div>
          </section>

          <Divider />

          {/* INSTAGRAM */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-purple-500/20 text-purple-400 p-2.5 rounded-xl"><Instagram size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">Instagram Performance</h2>
                <span className="text-white/40 text-sm font-mono">@thesachindahiya</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
                <StatCard icon={Play} iconColor="bg-purple-500/20 text-purple-300" label="Reels" value={61} subtext="+ 10 carousels = 71 posts" />
                <StatCard icon={Eye} iconColor="bg-pink-500/20 text-pink-300" label="Total Plays" value={7225} subtext="Public play counter" />
                <StatCard icon={Heart} iconColor="bg-rose-500/20 text-rose-300" label="Likes" value={480} subtext="Aggregate across posts" />
                <StatCard icon={MessageCircle} iconColor="bg-amber-500/20 text-amber-300" label="Comments" value={149} subtext="Conversations + CTA triggers" />
              </div>
              <div className="mb-6 flex items-center gap-3">
                <h3 className="text-xl font-bold text-white">Top 5 Reels</h3>
                <span className="bg-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-purple-500/30">Instagram</span>
              </div>
              <ReelGrid reels={igReels} metricLabel="Plays" />
              <p className="text-white/40 text-xs mt-4 italic">Carousels (10 this window) build saves — an Insights-only metric that isn't public, so their value is understated here.</p>
            </div>
          </section>

          <Divider />

          {/* LINKEDIN */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-sky-500/20 text-sky-400 p-2.5 rounded-xl"><Linkedin size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">LinkedIn Performance</h2>
                <span className="text-white/40 text-sm font-mono">company/scale-ed</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
                <StatCard icon={Play} iconColor="bg-sky-500/20 text-sky-300" label="Posts Shipped" value={60} subtext="Last 3 months" />
                <StatCard icon={Heart} iconColor="bg-rose-500/20 text-rose-300" label="Reactions" value={106} subtext="Likes + celebrate + insight" />
                <StatCard icon={MessageCircle} iconColor="bg-amber-500/20 text-amber-300" label="Comments" value={2} subtext="Direct replies" />
                <StatCard icon={Repeat2} iconColor="bg-emerald-500/20 text-emerald-300" label="Reposts" value={16} subtext="Network amplification" />
              </div>
              <div className="mb-6 flex items-center gap-3">
                <h3 className="text-xl font-bold text-white">Top Posts</h3>
                <span className="bg-sky-500/20 text-sky-300 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-sky-500/30">LinkedIn</span>
              </div>
              <LinkedInGrid posts={liPosts} />
              <p className="text-white/40 text-xs mt-4 italic">Impressions are private (owner-only). Pull from LinkedIn Analytics export to complete the picture.</p>
            </div>
          </section>

          <Divider />

          {/* X */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-slate-500/20 text-slate-200 p-2.5 rounded-xl"><AtSign size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">X / Twitter Performance</h2>
                <span className="text-white/40 text-sm font-mono">@scalingedtechs</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <StatCard icon={Play} iconColor="bg-slate-500/20 text-slate-200" label="Posts" value={5} subtext="Last 3 months" />
                <StatCard icon={Eye} iconColor="bg-pink-500/20 text-pink-300" label="Total Views" value={120} subtext="Effectively dormant channel" />
              </div>
              <p className="text-white/40 text-sm mt-6">X is 4th of 5 in priority and barely active. Worth activating only if it becomes a focus — current effort is best spent on YouTube + Instagram.</p>
            </div>
          </section>

          <Divider />

          {/* WHAT THE DATA SAYS */}
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-amber-500/20 text-amber-300 p-2.5 rounded-xl"><TrendingUp size={22} /></div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">What the data is telling us</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <GlassCard>
                  <p className="text-red-300 text-xs font-bold uppercase tracking-widest mb-2">Engine 01</p>
                  <h3 className="text-white text-lg font-bold mb-2">YouTube is the most reliable channel</h3>
                  <p className="text-white/70 text-sm leading-relaxed">39.5K views at a steady ~534/video, with the fewest flops. The same shorts travel more dependably here than anywhere else. Keep YouTube as the consistency backbone and double the long-form trust content the questionnaire calls for.</p>
                </GlassCard>
                <GlassCard>
                  <p className="text-blue-300 text-xs font-bold uppercase tracking-widest mb-2">Reach 02</p>
                  <h3 className="text-white text-lg font-bold mb-2">Facebook owns the ceiling</h3>
                  <p className="text-white/70 text-sm leading-relaxed">26.7K views, with the free-cash-flow and pricing-reset reels both breaking past 6K. Facebook's reel algorithm clearly favours this niche right now. Cross-post every winner to FB Reels within 24 hours of release.</p>
                </GlassCard>
                <GlassCard>
                  <p className="text-purple-300 text-xs font-bold uppercase tracking-widest mb-2">Runway 03</p>
                  <h3 className="text-white text-lg font-bold mb-2">Instagram is the biggest growth opportunity</h3>
                  <p className="text-white/70 text-sm leading-relaxed">Daily posting, but reels average ~118 plays — the smallest reach of any channel. The funnel works (comments convert to DMs), so the lever is top-of-funnel: stronger 3-second hooks and reverse-engineering the few reels that travelled, like the EdTech "two leagues" and PhysicsWallah news pegs.</p>
                </GlassCard>
                <GlassCard>
                  <p className="text-amber-300 text-xs font-bold uppercase tracking-widest mb-2">Funnel 04</p>
                  <h3 className="text-white text-lg font-bold mb-2">The comment → DM funnel is working</h3>
                  <p className="text-white/70 text-sm leading-relaxed">Most comments are CTA keyword-triggers (AI ×15, CaseStudy, Scale, Claude), plus real inbound: a high-ticket closer and leads routing themselves to DMs. Even at low view counts, content is converting. Keep one clear keyword-CTA on every reel.</p>
                </GlassCard>
                <GlassCard className="md:col-span-2">
                  <p className="text-sky-300 text-xs font-bold uppercase tracking-widest mb-2">LinkedIn 05</p>
                  <h3 className="text-white text-lg font-bold mb-2">LinkedIn is shipping but not pulling yet</h3>
                  <p className="text-white/70 text-sm leading-relaxed">60 posts for 106 reactions over 3 months — the content is going out, but engagement on the company page is thin and trending down. Two moves: sharpen the post tone for founders/operators, and grow the page via targeted outreach to the same audience that already engages with Sachin's reels. (Impressions are private — pull the Analytics export for the full read.)</p>
                </GlassCard>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  );
}
